/*
 * File: Learn.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 18, 2025 at 12:53 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	useContext, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Masonry from 'react-responsive-masonry';

// Utilities
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useSearchParams } from 'react-router-dom';
import { buildDynamicMeta } from '../../../components/Meta';

// Services
import { fetchQuestionPreviews } from '../../../services/question';
import { recordView } from '../../../services/view';

// Store
import { fetchTags } from '../../../store/slices/questionTag/questionTag.slice';
import {
	updateQuestions, updateActionSortBy, updateFeaturedQuestions, updateActionSelectedTags
} from '../../../store/slices/question/question.slice';

// Context
import ActionContext from '../../../context/SharedActionContext';

// Components
import {
	Meta,
	AppNavigation,
	SchemaScript,
	Typography,
	QuestionCard,
	Button,
	InfiniteScroll,
	Tag,
	FeedActionDropdown,
	CommunityPanel,
	TextArea,
	SuggestedQuestionsWidget,
	ResponsiveMasonry
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES, SOURCES } from '../../../../constants/base.constants';
import { QUESTION_FEED_SORT_BY } from '../../../../constants/feed.constants';
import { PANEL_VARIANTS } from '../../../components/CommunityPanel/CommunityPanel';

// Styles
import * as S from './Learn.styles';


/**
 * Constants
 */

const PLACEHOLDER_TAGS = [
	{ label: 'Making sense of childhood experiences' },
	{ label: 'Making sense of adult experiences' },
	{ label: 'Navigating relationships after trauma' },
	{ label: 'Sex, sexuality, and intimacy after trauma' },
	{ label: 'Managing emotions and how you feel in your body' },
	{ label: 'Understanding specific types of trauma and harm' },
	{ label: 'How to support survivors and be an ally' },
	{ label: 'Sharing your story with others' },
];


/**
 * Component
 */

const Learn = ({ meta: m }) => {

	// Get context from navigation parent
	const {
		dispatch: actionDispatch
	} = useContext(ActionContext);

	// Get questions from hook
	const {
		feedStatus,
		featuredStatus,
		items: questions,
		featuredItems: featuredQuestions,
		actionSortBy,
		actionSelectedTags
	} = useSelector((state) => state.question);

	// Create state
	const [currentPage, setCurrentPage] = useState(0);
	const [hasMoreToLoad, setMoreToLoad] = useState(questions.length === 0 || questions.length > 10);
	const [isFeedLoading, setIsFeedLoading] = useState(false);
	const [searchText, setSearchText] = useState(null);

	// Get search parameters
	const [searchParams, setSearchParams] = useSearchParams({});

	// Get query parameters
	const searchQuery = Object.fromEntries([...searchParams]);

	// Get current locale from hook
	const locale = useSelector((state) => state.locale.value);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current question tags from hook
	const { status: tagStatus, value: questionTags } = useSelector((state) => state.questionTag);

	// Create references
	const inputRef = useRef();
	const firstUpdate = useRef(true);

	// Use hooks
	const dispatch = useDispatch();

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Handle on input change action
	const handleOnChange = (event) => {
		const { value } = event.target;
		setSearchText(value);
	};

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageLearn.metaTitle', { partnerName: partner.name }),
		description: t('pageLearn.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Create partner domain
	const partnerDomain = useMemo(() => {
		let domain = `https://${partner.domain}`;
		if (partner.redirect_to_subdomain) {
			domain = `https://${partner.redirect_to_subdomain}.${partner.domain}`;
		}
		return domain;
	}, [partner]);

	// Create feed action options
	const feedSortByOptions = useMemo(() => QUESTION_FEED_SORT_BY.map((option) => ({
		label: t(`feedActions.${option}`),
		value: option
	})), [t]);

	// Handle fetch questions
	const fetchQuestions = async ({ page }) => {

		// Update state
		setIsFeedLoading(true);

		// Fetch question previews
		const { data: { result: questionPreviews } } = await fetchQuestionPreviews({
			page,
			sortBy: actionSortBy,
			tags: actionSelectedTags.map((tt) => tt.tag_id)
		});

		// Update question
		dispatch(updateQuestions(page === 0 ? questionPreviews : [...questions, ...questionPreviews]));

		// Update page state
		setCurrentPage(page);

		// Update more to load state
		setMoreToLoad(questionPreviews.length > 10);

		// Update state
		setIsFeedLoading(false);
	};

	// Handle fetch featured questions
	const fetchFeaturedQuestions = async () => {

		// Fetch question previews
		const { data: { result: questionPreviews } } = await fetchQuestionPreviews({
			page: 0,
			isFeatured: true
		});

		// Update question
		dispatch(updateFeaturedQuestions(questionPreviews));
	};

	// Handle ask question from prompt
	const promptAskQuestion = () => {
		actionDispatch({
			type: 'OPEN_QUESTION_CREATOR_MODAL',
			payload: { questionText: searchText }
		});
	};

	// Handle update tags
	const updateTags = (tag) => {

		// Initialize updated tag array
		const tagArray = [tag];

		// Update search query parameters
		const { tags, ...additionalParams } = searchQuery;
		setSearchParams({ ...additionalParams, ...tagArray.length > 0 ? { tags: tagArray.map((tt) => tt.slug).join(',') } : undefined });

		// Update selected tags
		dispatch(updateActionSelectedTags(tagArray));
	};

	// Handle actions on filter change
	useEffect(() => {
		if (!firstUpdate.current) {

			// Fetch questions if necessary
			if (feedStatus === 'final') {

				// Fetch questions
				fetchQuestions({ page: 0 });
			}
		}

		// Update first update
		firstUpdate.current = false;

	}, [actionSortBy, actionSelectedTags]); // eslint-disable-line react-hooks/exhaustive-deps

	// Handle actions on tag status change
	useEffect(() => {
		if (tagStatus === 'final' && feedStatus === 'final') {

			// Set tags from search query if necessary
			if (searchQuery && searchQuery.tags) {

				// Split tags
				const queryTags = searchQuery.tags.split(',').map((tt) => tt.trim());

				// Update selected tags
				dispatch(updateActionSelectedTags(questionTags.filter(({ slug }) => queryTags.includes(slug))));
			}
		}
	}, [tagStatus, feedStatus]);

	// Handle actions on feed status change
	useEffect(() => {

		// Fetch questions if necessary
		if (feedStatus !== 'final') {

			// Fetch questions
			fetchQuestions({ page: currentPage });
		}

	}, [feedStatus]);

	// Handle actions on featured feed status change
	useEffect(() => {

		// Fetch featured questions if necessary
		if (featuredStatus !== 'final') {

			// Fetch featured questions
			fetchFeaturedQuestions();
		}

	}, [featuredStatus]);

	// Handle actions on component load
	useEffect(() => {

		// Fetch question tags if necessary
		if (tagStatus !== 'final') {

			// Fetch question tags
			dispatch(fetchTags());
		}

		// Set timeout
		setTimeout(() => {

			// Focus input
			inputRef.current?.focus();

		}, 100);

		// Record view
		recordView({
			source: SOURCES.QUESTIONS,
			urlPath: window.location.pathname
		});

	}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Schema.org */}
			<SchemaScript schema={{
				'@context': 'http://schema.org',
				'@type': 'BreadcrumbList',
				'@id': `${partnerDomain}/learn/#BreadcrumbList`,
				itemListElement: [
					{
						'@type': 'ListItem',
						position: 1,
						item: {
							'@id': `${partnerDomain}/learn/#ListItem`,
							name: buildDynamicMeta(meta.title, { partner }),
							url: `${partnerDomain}${locale.localePath}/learn`
						}
					}
				],
			}}
			/>
			<SchemaScript schema={{
				'@context': 'https://schema.org',
				'@type': 'FAQPage',
				'@id': `${partnerDomain}/learn/#BreadcrumbList`,
				name: buildDynamicMeta(meta.title, { partner }),
				description: buildDynamicMeta(meta.description, { partner }),
				url: `${partnerDomain}${locale.localePath}/learn`,
				inLanguage: locale.localeBaseId,
				isPartOf: {
					'@id': `${partnerDomain}/#WebSite`
				},
				publisher: {
					'@id': `${partner.website}/#Organization`,
				},
				breadcrumb: {
					'@id': `${partnerDomain}/learn/#BreadcrumbList`
				},
				mainEntity: questions?.map((question) => ({
					'@type': 'Question',
					'@id': `https://${partnerDomain}/answer/${question.slug}/#Question`,
					name: question.question_locales[locale.localeBaseId],
					url: `https://${partnerDomain}${locale.localePath}/answer/${question.slug}`,
					acceptedAnswer: {
						'@type': 'Answer',
						text: question?.analytics_answer_locales[locale.localeBaseId]
					}
				}))
			}}
			/>

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Header */}
					<S.Header>

						{/* Title */}
						<Typography tag="h1" weight="black">
							<Trans t={t} i18nKey="pageLearn.title">
								Questions & Answers
							</Trans>
						</Typography>

						{/* Ask Button */}
						<Button
							size="large"
							onClick={() => {
								actionDispatch({
									type: 'OPEN_QUESTION_CREATOR_MODAL',
									payload: {}
								});
							}}
						>
							<Trans t={t} i18nKey="pageLearn.askQuestionButton">
								Ask a question
							</Trans>
						</Button>

					</S.Header>

					{/* Panel Container */}
					<S.PanelContainer>

						{/* Content Panel */}
						<S.ContentPanel>

							{/* Section - Search */}
							<S.FeatureSection>

								{/* Title */}
								<Typography tag="h3" weight="black">
									<Trans t={t} i18nKey="pageLearn.searchTitle">
										What are you interested in?
									</Trans>
								</Typography>

								{/* Search Widget */}
								<S.SearchWidget>

									{/* Search bar */}
									<TextArea
										placeholder={t('pageLearn.searchPlaceholder')}
										name="search"
										type="text"
										rows={1}
										characterLimit={500}
										defaultHideCharacterLimit
										value={searchText || ''}
										onChange={handleOnChange}
										smartField
										ref={inputRef}
										autoResize
										size="small"
									/>

									{/* Suggested Questions Widget */}
									<S.SuggestionContainer>
										<SuggestedQuestionsWidget prompt={searchText} />
									</S.SuggestionContainer>

									{/* Ask Button */}
									{searchText && searchText.length > 20 && (searchText.includes('?') || searchText.includes('¿')) && (
										<S.AskPrompt>
											<Button variation="secondary" onClick={promptAskQuestion}>
												<Typography tag="span" weight="semibold">
													<Trans t={t} i18nKey="pageLearn.askQuestionButtonShort">
														Ask question
													</Trans>
												</Typography>
												<FontAwesomeIcon icon={['fas', 'arrow-right-long']} />
											</Button>
										</S.AskPrompt>
									)}

								</S.SearchWidget>
							</S.FeatureSection>

							{/* Section - Featured Questions */}
							{featuredQuestions.length > 0 && (
								<S.FeatureSection>

									{/* Title */}
									<Typography tag="h3" weight="black">
										<Trans t={t} i18nKey="pageLearn.featuredTitle">
											Featured
										</Trans>
									</Typography>

									{/* Content Masonry */}
									<ResponsiveMasonry>
										<Masonry gutter="22px">
											{featuredQuestions.map((question, index) => (
												<QuestionCard
													key={question.question_id}
													question={question}
													source={SOURCES.QUESTIONS}
													index={index}
												/>
											))}
										</Masonry>
									</ResponsiveMasonry>
								</S.FeatureSection>
							)}

							{/* Section - Question Tags */}
							<S.FeatureSection>

								{/* Title */}
								<Typography tag="h3" weight="black">
									<Trans t={t} i18nKey="pageLearn.tagsTitle">
										Topics
									</Trans>
								</Typography>

								{/* Question Tags */}
								{tagStatus === 'final' ? (
									<S.TagContainer>
										{questionTags.map((tag) => (
											<Tag
												key={tag.tag_id}
												isActive={actionSelectedTags.some((tt) => tt.tag_id === tag.tag_id)}
												className="animate"
												onClick={() => { updateTags(tag); }}
											>
												<Typography tag="p" variation="2">{tag.title_locales[locale.localeBaseId]}</Typography>
											</Tag>
										))}
									</S.TagContainer>
								) : (
									<S.TagContainer>
										{PLACEHOLDER_TAGS.map((placeholder) => (
											<S.TagPlaceholder className="shimmer shimmer-element" key={placeholder.label}>
												<Typography tag="p" variation="2">{placeholder.label}</Typography>
											</S.TagPlaceholder>
										))}
									</S.TagContainer>
								)}
							</S.FeatureSection>

							{/* Section - Questions */}
							<S.FeatureSection>

								{/* Feed Action Bar */}
								<S.FeedActionBar>

									{/* Feed Sort Action */}
									<FeedActionDropdown
										title={t('feedActions.sortByTitle')}
										selected={actionSortBy}
										selectedTitle={t(`feedActions.${actionSortBy}`)}
										options={feedSortByOptions}
										multipleSelection={false}
										widgetWidth={170}
										onSelect={({ value }) => { dispatch(updateActionSortBy(value)); }}
									/>

								</S.FeedActionBar>

								{/* Infinite Scroll */}
								<InfiniteScroll
									dataLength={questions.length}
									next={() => { if (!isFeedLoading) fetchQuestions({ page: currentPage + 1 }); }}
									refresh={async () => { await fetchQuestions({ page: 0 }); return true; }}
									loader={(
										<ResponsiveMasonry style={{ marginTop: questions.length > 0 ? '22px' : '0px' }}>
											<Masonry gutter="22px">
												<QuestionCard isPlaceholder index={0} />
												<QuestionCard isPlaceholder index={1} />
												<QuestionCard isPlaceholder index={2} />
												<QuestionCard isPlaceholder index={3} />
												<QuestionCard isPlaceholder index={4} />
												<QuestionCard isPlaceholder index={5} />
												<QuestionCard isPlaceholder index={6} />
												<QuestionCard isPlaceholder index={7} />
												<QuestionCard isPlaceholder index={8} />
											</Masonry>
										</ResponsiveMasonry>
									)}
									hasMore={hasMoreToLoad}
								>

									{/* Content Masonry */}
									<ResponsiveMasonry>
										<Masonry gutter="22px">
											{questions.map((question, index) => (
												<QuestionCard
													key={question.question_id}
													question={question}
													source={SOURCES.QUESTIONS}
													index={index}
												/>
											))}
										</Masonry>
									</ResponsiveMasonry>

								</InfiniteScroll>

							</S.FeatureSection>
						</S.ContentPanel>

						{/* Community Panel */}
						<CommunityPanel variant={PANEL_VARIANTS.LEARN} offset={70} className="communityPanel" />

					</S.PanelContainer>
				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

Learn.propTypes = {
	meta: PropTypes.shape()
};
Learn.defaultProps = {
	meta: {}
};


/**
 * Exports
 */

export default Learn;
